/* Bilder für das PDF vorbereiten.
 *
 * jsPDF bettet nur JPEG und PNG ein. Fotos und Vereinslogos liegen als WebP vor (abgeleitete Fassungen, siehe
 * domain/photoVariants.js); sie werden hier über ein Canvas in JPEG (Foto, ohne Transparenz) bzw. PNG (Logo, mit
 * Transparenz) gewandelt. Foto: Ausschnitt 3:4, waagerecht Mitte, senkrecht oben (coverRect) — wie die Anzeige.
 *
 * Die Browser-Teile (createImageBitmap, Canvas) kommen als `env` herein; so ist die Umwandlung ohne Browser
 * prüfbar. Schlägt etwas fehl, wirft die Funktion — der Aufrufer nimmt dann den Platzhalter mit Initialen.
 */

import { coverRect } from '../domain/photoVariants.js';

/* Vektorbild ohne eigene Größe: auf diese Kantenlänge gesetzt */
const VECTOR_FALLBACK_SIDE = 256;

const browserEnv = () => ({
  decode: (blob) => createImageBitmap(blob),
  /* createImageBitmap kann kein SVG (und manche exotischen Dateien) lesen — ein Bildelement kann es, so wie die App
     die Logos anzeigt. Skripte und fremde Adressen sind in einem <img> abgeschaltet. */
  decodeImage: async (blob) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.src = url;
    try {
      await img.decode();
    } catch (e) {
      URL.revokeObjectURL(url);
      throw e;
    }
    return { source: img, width: img.naturalWidth || VECTOR_FALLBACK_SIDE, height: img.naturalHeight || VECTOR_FALLBACK_SIDE, close: () => URL.revokeObjectURL(url) };
  },
  createCanvas: (w, h) => {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  },
});

/** Zielgröße unter Beibehaltung des Seitenverhältnisses, höchstens maxWidth breit, nie hochgerechnet (außer upscale: Vektorbilder). */
export function scaleTo(width, height, maxWidth, { upscale = false } = {}) {
  const s = upscale ? maxWidth / width : Math.min(1, maxWidth / width);
  return { width: Math.max(1, Math.round(width * s)), height: Math.max(1, Math.round(height * s)) };
}

/** Hat ein RGBA-Pixelfeld (Uint8ClampedArray) mindestens einen Pixel mit Alpha < 255? */
export function hasTransparency(data) {
  for (let i = 3; i < data.length; i += 4) if (data[i] < 255) return true;
  return false;
}

/**
 * Foto -> Data-URL im Format 3:4: JPEG, wenn das Foto vollständig deckend ist (kleine Datei, wie bisher), PNG mit
 * Transparenz, sobald ein Pixel durchscheint (freigestelltes Foto — die Fläche der Karte soll durchscheinen).
 * createImageBitmap kann in manchen Browsern (Safari) Dateien nicht lesen, die ein Bildelement liest; dann gilt wie bei
 * den Logos der Weg über env.decodeImage. Erst wenn auch das scheitert, wirft die Funktion (Platzhalter).
 *
 * @param {Blob} blob
 * @param {{ maxWidth?: number, quality?: number, background?: string, env?: object }} [opts]
 * @returns {Promise<{ dataUrl: string, format: 'JPEG'|'PNG', width: number, height: number }>}
 */
export async function photoToJpeg(blob, { maxWidth = 480, quality = 0.78, background = '#F4F5F8', env = browserEnv() } = {}) {
  let bmp;
  try {
    bmp = await env.decode(blob);
  } catch (e) {
    if (!env.decodeImage) throw e;
    bmp = await env.decodeImage(blob);
  }
  try {
    const crop = coverRect(bmp.width, bmp.height);
    const out = scaleTo(crop.sw, crop.sh, maxWidth);
    const canvas = env.createCanvas(out.width, out.height);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bmp.source || bmp, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, out.width, out.height);
    /* Transparenz prüfen. Lässt sich das Canvas nicht auslesen, gilt "unbekannt": JPEG mit hellem Grund hinter dem Bild
       (statt Schwarz), wie vor F17b. */
    let alpha = null;
    try {
      alpha = hasTransparency(ctx.getImageData(0, 0, out.width, out.height).data);
    } catch {
      alpha = null;
    }
    if (alpha) {
      const png = canvas.toDataURL('image/png');
      if (!/^data:image\/png;base64,/.test(png)) throw new Error('encodeFailed');
      return { dataUrl: png, format: 'PNG', width: out.width, height: out.height };
    }
    if (alpha === null) {
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, out.width, out.height);
    }
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (!/^data:image\/jpeg;base64,/.test(dataUrl)) throw new Error('encodeFailed');
    return { dataUrl, format: 'JPEG', width: out.width, height: out.height };
  } finally {
    if (typeof bmp.close === 'function') bmp.close();
  }
}

/**
 * Vereinslogo -> PNG-Data-URL (Transparenz bleibt), unverzerrt, höchstens maxWidth breit. PNG, WebP und JPEG gehen über
 * createImageBitmap; SVG (und alles, was es nicht liest) über ein Bildelement (env.decodeImage) und wird dabei scharf auf
 * maxWidth gerastert (Vektor, darf größer werden als die Angabe in der Datei). Eine Stelle für alle Vereinslogos der Karte.
 * Wirft, wenn das Bild nicht lesbar ist — der Aufrufer zeigt dann nur den Namen.
 */
export async function logoToPng(blob, { maxWidth = 128, env = browserEnv() } = {}) {
  const isSvg = /svg/i.test(String((blob && blob.type) || ''));
  let bmp;
  const vector = isSvg;
  if (isSvg && env.decodeImage) {
    bmp = await env.decodeImage(blob);
  } else {
    try {
      bmp = await env.decode(blob);
    } catch (e) {
      if (!env.decodeImage) throw e;
      bmp = await env.decodeImage(blob);
    }
  }
  try {
    const out = scaleTo(bmp.width, bmp.height, maxWidth, { upscale: vector });
    const canvas = env.createCanvas(out.width, out.height);
    const ctx = canvas.getContext('2d');
    const src = bmp.source || bmp;
    if (vector) ctx.drawImage(src, 0, 0, out.width, out.height);
    else ctx.drawImage(src, 0, 0, bmp.width, bmp.height, 0, 0, out.width, out.height);
    const dataUrl = canvas.toDataURL('image/png');
    if (!/^data:image\/png;base64,/.test(dataUrl)) throw new Error('encodeFailed');
    return { dataUrl, format: 'PNG', width: out.width, height: out.height };
  } finally {
    if (typeof bmp.close === 'function') bmp.close();
  }
}
